import Close from '@mui/icons-material/Close';
import Replay from '@mui/icons-material/Replay';
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import SignaturePad from 'signature_pad';

import { ActionComponentProps } from '../index';

type DialogSignatureConfirmationProps = ActionComponentProps & {
  persist: boolean;
  count?: number;
  onConfirm: (imageBase64: string) => void;
};

export const DialogSignatureConfirmation = ({
  mailpieces,
  actionStarted,
  persist,
  count = 0,
  onActionFinish,
  onConfirm,
}: DialogSignatureConfirmationProps) => {
  const theme = useTheme();

  const signaturePadWrapperRef = useRef<HTMLDivElement>(null);

  // Signature Pad library documentation: https://github.com/szimek/signature_pad
  const [signaturePad, setSignaturePad] = useState<SignaturePad | null>(null);
  const [signaturePadSize, setSignaturePadSize] = useState<{ width: number; height: number }>({
    width: 780,
    height: 780 / 2.644,
  });
  const [signatureBase64PNG, setSignatureBase64PNG] = useState<string>('');
  const [isSigned, setIsSigned] = useState<boolean>(false);

  const setCanvasRef = useCallback(
    (node: HTMLCanvasElement | null) => {
      const signaturePadInstance = node
        ? new SignaturePad(node, { backgroundColor: theme.palette.background.neutral })
        : null;

      setSignaturePad(signaturePadInstance);
    },
    [theme.palette.background.neutral],
  );

  const resizeObserver = useMemo(
    () =>
      new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          setSignaturePadSize({
            width: entry.contentRect.width,
            height: entry.contentRect.width / 2.644,
          });
        });
      }),
    [],
  );

  useEffect(() => {
    if (signaturePad) {
      signaturePad.addEventListener('endStroke', () => {
        setIsSigned(!signaturePad.isEmpty());
        setSignatureBase64PNG(signaturePad.toDataURL());
      });

      return () => {
        signaturePad.off();
        setSignatureBase64PNG('');
      };
    } else {
      setIsSigned(false);
    }
  }, [signaturePad]);

  useEffect(() => {
    if (!signaturePadWrapperRef.current) return;
    resizeObserver.observe(signaturePadWrapperRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [resizeObserver, signaturePad]);

  // Handling resize of the Signature Pad canvas.
  // Just after resize restoring the signature image since
  // browsers automatically clear canvases on resize.
  useEffect(() => {
    if (!signaturePad || !signatureBase64PNG) return;
    signaturePad.clear();
    signaturePad.fromDataURL(signatureBase64PNG, {
      ratio: 1,
      width: signaturePadSize.width,
      height: signaturePadSize.height,
    });
  }, [signaturePad, signaturePadSize, signatureBase64PNG]);

  const clearSignature = () => {
    if (signaturePad) {
      signaturePad.clear();
      setSignatureBase64PNG('');
      setIsSigned(false);
    }
  };

  const submitSignature = () => {
    if (signaturePad) {
      onConfirm(signaturePad.toDataURL('image/jpeg'));
    }
  };

  return (
    <Dialog fullWidth maxWidth="md" open={actionStarted} onClose={() => !persist && onActionFinish()}>
      <DialogTitle>Complete with signature</DialogTitle>

      <DialogContent>
        <Typography variant="body1" color="text.secondary">
          Please ask <strong>{mailpieces[0].recipient ? mailpieces[0].recipient.displayName : 'recipient'}</strong> to
          sign on the line below
          {count ? (
            <>
              {' '}
              for <strong>{count}</strong> item(s)
            </>
          ) : (
            ''
          )}
          .
        </Typography>

        <Stack gap={3} alignItems="center" sx={{ borderRadius: 1, bgcolor: 'background.neutral', p: 3, mt: 3 }}>
          <Stack direction="row" alignItems="end" sx={{ width: '100%' }}>
            <Close color="disabled" />

            <Box ref={signaturePadWrapperRef} sx={{ flex: 1 }}>
              <canvas
                width={signaturePadSize.width}
                height={signaturePadSize.height}
                ref={setCanvasRef}
                style={{
                  maxWidth: '100%',
                  borderBottomWidth: 1,
                  borderBottomStyle: 'solid',
                  borderBottomColor: theme.palette.grey[500],
                }}
              />
            </Box>
          </Stack>

          <Button variant="text" color="inherit" startIcon={<Replay />} onClick={clearSignature}>
            Clear
          </Button>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="text" color="inherit" onClick={onActionFinish} disabled={persist}>
          Cancel
        </Button>
        <Button
          onClick={submitSignature}
          disabled={!isSigned || persist}
          loading={persist}
          startIcon={persist && <SaveIcon />}
          loadingPosition={persist ? 'start' : undefined}
          variant="contained"
          color="primary"
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};
